import { useEffect, useState, useMemo, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useSearchParams } from "react-router-dom";
import {
  Search, BookOpen, ChevronRight, Sparkles, Rocket, Users, Layers,
  Megaphone, FileText, GraduationCap, Crown, Shield, RefreshCw,
  Building2, Globe, Award, Package, CreditCard, GitBranch, FileSignature, Play, Bot, Flame, ClipboardList, Mail, Download, Compass,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { HELP_CATEGORIES, HELP_TOPICS, getVisibleTopics } from "../data/helpContent";
const ICONS = {
  Rocket, Users, Layers, Megaphone, FileText, GraduationCap, Crown, Shield,
  RefreshCw, Building2, Globe, Award, Package, CreditCard, GitBranch, FileSignature, BookOpen,
  Bot, Flame, Play, ClipboardList, Mail,
};

export default function HelpCenterPage() {
  const { user, axiosAuth } = useAuth();
  const { isDark } = useTheme();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [activeTopic, setActiveTopic] = useState(params.get("topic") || null);
  const [releaseNotes, setReleaseNotes] = useState(null);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState("");

  // Iter 255 — téléchargement des PDF de documentation plateforme
  const downloadDoc = async (kind) => {
    setDownloadingDoc(kind);
    try {
      const r = await api.get(`/platform-docs/${kind}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "commercial" ? "Blitzz-Plateforme-Presentation.pdf" : kind === "api-keys" ? "Blitzz-Cles-API-a-fournir.pdf" : "Blitzz-Guide-Utilisateur.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Téléchargement impossible pour le moment.");
    }
    setDownloadingDoc("");
  };

  const role = user?.role || "user";
  const businessRoles = user?.roles || [];
  const visibleTopics = useMemo(() => getVisibleTopics(role, businessRoles), [role, businessRoles]);

  // Filtre par recherche
  const filteredTopics = useMemo(() => {
    const out = {};
    const q = search.trim().toLowerCase();
    Object.entries(visibleTopics).forEach(([id, topic]) => {
      if (!q) { out[id] = topic; return; }
      const inTitle = topic.title.toLowerCase().includes(q);
      const inSections = (topic.sections || []).some(s =>
        s.title.toLowerCase().includes(q) || s.body.toLowerCase().includes(q)
      );
      if (inTitle || inSections) out[id] = topic;
    });
    return out;
  }, [visibleTopics, search]);

  // Group topics by category
  const grouped = useMemo(() => {
    const g = {};
    Object.entries(filteredTopics).forEach(([id, t]) => {
      if (!g[t.category]) g[t.category] = [];
      g[t.category].push({ id, ...t });
    });
    return g;
  }, [filteredTopics]);

  const handleSelectTopic = useCallback((id) => {
    setActiveTopic(id);
    setParams({ topic: id });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [setParams]);

  // Charger release notes au mount
  useEffect(() => {
    api.get("/help/release-notes")
      .then(r => setReleaseNotes(r.data))
      .catch(() => setReleaseNotes({ versions: [], raw: "" }));
  }, [api]);

  const activeTopicData = activeTopic ? HELP_TOPICS[activeTopic] : null;

  return (
    <div
      className={`min-h-screen ${isDark ? "bg-[#0e0a17] text-slate-100" : "bg-[#f5f5f7] text-[#1a1025]"}`}
      data-testid="help-center-page"
    >
      {/* Style adaptatif clair/sombre pour la page Centre d'aide */}
      <style>{`
        [data-help-center] .help-card {
          background: ${isDark ? "#14101c" : "#ffffff"};
          border-color: ${isDark ? "rgba(255,255,255,0.06)" : "#f3f4f6"};
        }
        [data-help-center] .help-text { color: ${isDark ? "#f1f5f9" : "#1a1025"}; }
        [data-help-center] .help-muted { color: ${isDark ? "#94a3b8" : "#6b7280"}; }
        [data-help-center] .help-soft { color: ${isDark ? "#cbd5e1" : "#374151"}; }
        [data-help-center] .help-input {
          background: ${isDark ? "#0b0811" : "#ffffff"};
          color: ${isDark ? "#f1f5f9" : "#1a1025"};
          border-color: ${isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb"};
        }
        [data-help-center] .help-input::placeholder { color: ${isDark ? "#64748b" : "#9ca3af"}; }
        [data-help-center] .help-divider { border-color: ${isDark ? "rgba(255,255,255,0.06)" : "#f3f4f6"}; }
        [data-help-center] .help-link {
          background: ${isDark ? "transparent" : "transparent"};
          color: ${isDark ? "#cbd5e1" : "#374151"};
        }
        [data-help-center] .help-link:hover {
          background: ${isDark ? "rgba(247,201,53,0.10)" : "#fef9e7"};
          color: ${isDark ? "#f7c935" : "#1a1025"};
        }
        [data-help-center] .help-link.active {
          background: ${isDark ? "rgba(247,201,53,0.18)" : "#fef9e7"};
          color: ${isDark ? "#f7c935" : "#1a1025"};
          border-left: 3px solid #f7c935;
        }
        [data-help-center] .help-section-title {
          color: ${isDark ? "#94a3b8" : "#6b7280"};
        }
        [data-help-center] .help-secondary-btn {
          background: ${isDark ? "#14101c" : "#ffffff"};
          border-color: ${isDark ? "rgba(255,255,255,0.10)" : "#e5e7eb"};
          color: ${isDark ? "#f1f5f9" : "#1a1025"};
        }
        [data-help-center] .help-secondary-btn:hover {
          border-color: #f7c935;
        }
        [data-help-center] .help-prose h1,
        [data-help-center] .help-prose h2,
        [data-help-center] .help-prose h3 { color: ${isDark ? "#f1f5f9" : "#1a1025"}; }
        [data-help-center] .help-prose p,
        [data-help-center] .help-prose li { color: ${isDark ? "#cbd5e1" : "#374151"}; }
        [data-help-center] .help-prose strong { color: ${isDark ? "#f1f5f9" : "#1a1025"}; }
        [data-help-center] .help-prose code {
          background: ${isDark ? "rgba(247,201,53,0.12)" : "#fef9e7"};
          color: ${isDark ? "#f7c935" : "#92400e"};
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 0.85em;
        }
      `}</style>

      <div data-help-center className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#f7c935] to-[#e6b825] flex items-center justify-center shadow-md">
              <BookOpen className="w-6 h-6 text-[#1a1025]" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black help-text leading-none">Centre d'aide Blitzz</h1>
              <p className="text-sm help-muted mt-1">Guides, tutoriels et notes de release • Adapté à votre rôle</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => downloadDoc("commercial")}
              disabled={downloadingDoc === "commercial"}
              className="flex items-center gap-2 px-4 py-2 rounded-lg help-secondary-btn border transition-colors text-sm font-bold shadow-sm disabled:opacity-50"
              data-testid="btn-download-commercial-pdf"
              title="Plaquette de présentation des fonctionnalités de la plateforme"
            >
              <Download className="w-4 h-4 text-[#f7c935]" />
              {downloadingDoc === "commercial" ? "Génération..." : "Plaquette PDF"}
            </button>
            <button
              onClick={() => downloadDoc("guide")}
              disabled={downloadingDoc === "guide"}
              className="flex items-center gap-2 px-4 py-2 rounded-lg help-secondary-btn border transition-colors text-sm font-bold shadow-sm disabled:opacity-50"
              data-testid="btn-download-guide-pdf"
              title="Guide utilisateur détaillé module par module"
            >
              <Download className="w-4 h-4 text-[#f7c935]" />
              {downloadingDoc === "guide" ? "Génération..." : "Guide PDF"}
            </button>
            <button
              onClick={() => downloadDoc("api-keys")}
              disabled={downloadingDoc === "api-keys"}
              className="flex items-center gap-2 px-4 py-2 rounded-lg help-secondary-btn border transition-colors text-sm font-bold shadow-sm disabled:opacity-50"
              data-testid="btn-download-api-keys-pdf"
              title="Liste des clés API tierces à fournir : où les obtenir et où les saisir"
            >
              <Download className="w-4 h-4 text-[#f7c935]" />
              {downloadingDoc === "api-keys" ? "Génération..." : "Clés API PDF"}
            </button>
            <button
              onClick={() => {
                // Effacer toutes les clés de tour pour tout retrigger
                try {
                  Object.keys(localStorage)
                    .filter((k) => k.startsWith("blitzz_") && k.includes("_tour_"))
                    .forEach((k) => localStorage.removeItem(k));
                  alert("Tutoriel réactivé. Vous serez guidé(e) pas à pas dès votre prochaine visite du Dashboard.");
                  window.location.href = "/dashboard";
                } catch {}
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#f7c935] to-[#e6b825] hover:from-[#e6b825] hover:to-[#d4a824] transition-all text-sm font-bold text-[#1a1025] shadow-sm"
              data-testid="btn-replay-tutorial"
              title="Réactive le tour guidé sur le Dashboard"
            >
              <Play className="w-4 h-4" />
              Activer le tutoriel
            </button>
            <button
              onClick={() => setShowReleaseNotes(!showReleaseNotes)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg help-secondary-btn border transition-colors text-sm font-bold shadow-sm"
              data-testid="btn-release-notes"
            >
              <Sparkles className="w-4 h-4 text-[#f7c935]" />
              {showReleaseNotes ? "Masquer" : "Notes de release"}
              {releaseNotes?.versions?.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#f7c935] text-[#1a1025] font-black">{releaseNotes.versions.length}</span>
              )}
            </button>
          </div>
        </div>

        {/* Release Notes panel */}
        {showReleaseNotes && (
          <div className="help-card rounded-2xl shadow-sm border p-6 mb-8" data-testid="release-notes-panel">
            <h2 className="text-lg font-black help-text mb-4 flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-[#f7c935]" /> Historique des versions
            </h2>
            {!releaseNotes ? (
              <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
            ) : releaseNotes.versions.length === 0 ? (
              <p className="text-sm help-muted">Aucune note de release pour le moment.</p>
            ) : (
              <div className="space-y-5 max-h-[500px] overflow-y-auto pr-2">
                {releaseNotes.versions.map((v, i) => (
                  <div key={i} className="border-l-4 border-[#f7c935] pl-4 py-1" data-testid={`release-version-${i}`}>
                    <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                      <span className="text-base font-black help-text">{v.version}</span>
                      <span className="text-xs help-muted">{v.date}</span>
                      <span className="text-sm help-soft font-medium">— {v.title}</span>
                    </div>
                    {v.summary && <p className="text-sm help-muted mb-2 italic">{v.summary}</p>}
                    {v.items?.length > 0 && (
                      <ul className="space-y-1 mt-2">
                        {v.items.slice(0, 6).map((item, j) => (
                          <li key={j} className="text-xs help-soft flex items-start gap-2">
                            <span className="text-[#f7c935] flex-shrink-0">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                        {v.items.length > 6 && (
                          <li className="text-[10px] help-muted italic">+ {v.items.length - 6} autres...</li>
                        )}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 help-muted" />
          <Input
            placeholder="Rechercher dans l'aide... (ex: relances, certifications, Stripe)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 help-input focus:border-[#f7c935] h-12 text-sm"
            data-testid="help-search-input"
          />
        </div>

        <div className="grid lg:grid-cols-[300px_1fr] gap-6">
          {/* Sidebar : navigation par catégorie */}
          <aside className="help-card rounded-2xl shadow-sm border p-4 lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto lg:sticky lg:top-4" data-testid="help-sidebar">
            {HELP_CATEGORIES.map(cat => {
              const topics = grouped[cat.id] || [];
              if (topics.length === 0 && search) return null;
              if (topics.length === 0) return null;
              const Icon = ICONS[cat.icon] || BookOpen;
              return (
                <div key={cat.id} className="mb-4">
                  <div className="flex items-center gap-2 px-2 py-1.5 mb-1">
                    <Icon className="w-3.5 h-3.5 text-[#f7c935]" />
                    <span className="text-[10px] uppercase font-black help-section-title tracking-wider">{cat.label}</span>
                  </div>
                  <div className="space-y-0.5">
                    {topics.map(t => {
                      const TIcon = ICONS[t.icon] || BookOpen;
                      const active = activeTopic === t.id;
                      return (
                        <button
                          key={t.id}
                          onClick={() => handleSelectTopic(t.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-2 help-link ${active ? "active font-bold" : ""}`}
                          data-testid={`help-topic-link-${t.id}`}
                        >
                          <TIcon className={`w-3.5 h-3.5 flex-shrink-0 ${active ? "text-[#f7c935]" : "help-muted"}`} />
                          <span className="flex-1 truncate">{t.title}</span>
                          {active && <ChevronRight className="w-3 h-3 text-[#f7c935] flex-shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {Object.keys(grouped).length === 0 && (
              <div className="text-center py-8 text-sm help-muted">
                Aucun résultat pour "{search}"
              </div>
            )}
          </aside>

          {/* Main : contenu du topic sélectionné */}
          <main className="help-card rounded-2xl shadow-sm border p-6 md:p-8 min-h-[60vh]" data-testid="help-content">
            {activeTopicData ? (
              <article className="help-prose">
                <div className="flex items-center gap-3 mb-2">
                  {(() => {
                    const Icon = ICONS[activeTopicData.icon] || BookOpen;
                    return (
                      <div className="w-10 h-10 rounded-xl bg-[#f7c935]/15 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#f7c935]" />
                      </div>
                    );
                  })()}
                  <h2 className="text-2xl font-black help-text">{activeTopicData.title}</h2>
                </div>
                <div className="text-xs help-muted mb-6 flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full font-medium ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
                    {HELP_CATEGORIES.find(c => c.id === activeTopicData.category)?.label}
                  </span>
                  <span>•</span>
                  <span>Audience : {(activeTopicData.audience || []).join(", ")}</span>
                </div>

                <div className="space-y-6">
                  {(activeTopicData.sections || []).map((s, i) => (
                    <section key={i} className="border-l-4 border-[#f7c935]/40 pl-4 py-1" data-testid={`help-section-${i}`}>
                      <h3 className="text-base font-black help-text mb-2">{s.title}</h3>
                      <p className="text-sm help-soft leading-relaxed whitespace-pre-line">{s.body}</p>
                      {s.video_url && (
                        <div className={`mt-3 aspect-video rounded-lg overflow-hidden ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
                          <iframe
                            src={s.video_url}
                            className="w-full h-full"
                            title={s.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      )}
                    </section>
                  ))}
                </div>

                {/* Print button */}
                <div className="mt-8 pt-6 border-t help-divider flex items-center justify-between">
                  <button
                    onClick={() => window.print()}
                    className={`text-xs px-3 py-1.5 rounded-lg transition-colors font-medium ${isDark ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                    data-testid="help-print-btn"
                  >
                    🖨 Imprimer cette page
                  </button>
                  <span className="text-[10px] help-muted">Mis à jour en juin 2026</span>
                </div>
              </article>
            ) : (
              // Vue par défaut : grille des topics
              <div data-testid="help-default-view">
                <h2 className="text-xl font-black help-text mb-2">Bienvenue dans le centre d'aide</h2>
                <p className="text-sm help-muted mb-6">Sélectionnez une catégorie à gauche ou utilisez la recherche pour trouver rapidement une réponse. Le contenu s'adapte à votre rôle ({role}{businessRoles.length > 0 ? ` + ${businessRoles.join(", ")}` : ""}).</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(grouped).flatMap(([catId, topics]) =>
                    topics.slice(0, 2).map(t => {
                      const Icon = ICONS[t.icon] || BookOpen;
                      return (
                        <button
                          key={t.id}
                          onClick={() => handleSelectTopic(t.id)}
                          className={`text-left p-4 rounded-xl border hover:border-[#f7c935] hover:shadow-md transition-all group ${isDark ? "border-white/5 hover:bg-white/5" : "border-gray-100"}`}
                          data-testid={`help-card-${t.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-lg bg-[#f7c935]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#f7c935]/20 transition-colors">
                              <Icon className="w-4 h-4 text-[#f7c935]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold help-text mb-1">{t.title}</div>
                              <div className="text-[11px] help-muted line-clamp-2">{(t.sections?.[0]?.body || "").slice(0, 100)}...</div>
                            </div>
                            <ChevronRight className="w-4 h-4 help-muted group-hover:text-[#f7c935] flex-shrink-0 mt-2" />
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
